import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, flatMap, catchError } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from './models/bookmark.model';
import { Observable, forkJoin, throwError, of } from 'rxjs';
import { StoryModel } from '../story/models/story.model';
import { BookmarkEntryModel } from './models/bookmark.entry.model';
import { BookmarkGroupModel } from './models/bookmark.group.model';
import { StoryChapterThumbnail } from 'src/app/story/models/story-chapter-thumbnail.model';
import { SerializeModuleService } from '../edit/serialize-module.service';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { ExploreService } from '../explore/explore.service';
import { StoryType } from '../story/models/story-type.enum';

import { endsWith } from 'lodash';
import { Store } from '@ngxs/store';
import { StoryState } from '../story/state/story.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';

const bookmarkServiceURL = '/ws/user/bookmark/v1';
const thumbnailServiceURL = '/ws/thumbs/bookmarks';
const favoritesServiceURL = '/ws/user/favorite';

const formHeaders = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8'
  })
};

export enum BookmarkCategory {
  Private = 'private',
  Group = 'enterprise',
  Public = 'public'
}

@Injectable({
  providedIn: 'root'
})
export class BookmarksService {

  constructor(
    private store: Store,
    private http: HttpClient,
    private serializeModuleService: SerializeModuleService,
    private exploreService: ExploreService
  ) { }

  /**
   * Gets the bookmark data by the specified id
   * @param id The bookmark id
   */
  getBookmarkData(id: string): Observable<StoryModel> {
    return this.http.get(`/search/bookmarks/data?be=${id}`)
      .pipe(
        map((result: any) => {
          result = result.bookmark;
          result.moduleId = result.customData ? result.customData.moduleId : result.modules.activeModules[0];

          return plainToClass(StoryModel, result as Object);
        })
      );
  }

  /**
   * Gets the bookmark model data by specified id
   * @param id The bookmark id
   */
  getBookmarkModel(id: string): Observable<BookmarkModel> {
    return this.http.get(`/ws/search/bookmarks?eid=${id}`)
      .pipe(
        map((result: any) => plainToClass(BookmarkModel, result.bookmark_info as object)),
        map((bookmark: BookmarkModel) => this.normalizeBookmarkModel(bookmark))
      );
  }

  /** Gets all user generated bookmarks by specified category
   * @param category Category of bookmarks
   * @param query Optional search query
   */
  getUserBookmarks(category: BookmarkCategory, query: string = ''): Observable<BookmarkModel[]> {
    return this.http.get(`/ws/search/bookmarks/${category}?q=${query}`)
      .pipe(
        map((results: object[]) => {
          return results
            .map((result) => plainToClass(BookmarkModel, result))
            .map((bookmark) => this.normalizeBookmarkModel(bookmark));
        })
      );
  }

  /**
   * Gets a list of user mapped custom conditions.
   */
  getUserConditions(category: BookmarkCategory, id?: number): Observable<BookmarkModel[]> {
    let moduleAccessorLevel = '';
    switch (category) {
      case BookmarkCategory.Private:
        moduleAccessorLevel = 'user';
        break;
      case BookmarkCategory.Group:
        moduleAccessorLevel = 'group';
        break;
      default:
        break;
    }
    return this.http.get(`/ws/search/conditions/private`)
      .pipe(
        // restrict by type + id
        map((results: Object[]) => {
          return results
            .filter((result: any) => {
              const matchedByType = result.module_accessor_level === moduleAccessorLevel;
              const matchedById = !id || result.module_accessor_id === id;
              return matchedByType && matchedById;
            });
        }),
        // to bookmarks
        map((results: Object[]) => plainToClass(BookmarkModel, results)),
        // normalize
        map((bookmarks: BookmarkModel[]) => {
          return bookmarks
            .map((bookmark) => this.normalizeBookmarkModel(bookmark));
        })
      );
  }

  /**
   * Gets a list of private bookmarks only accessible to user
   * @param query Optional search query
   */
  getPrivateBookmarks(query: string = ''): Observable<BookmarkModel[]> {
    return this.getUserBookmarks(BookmarkCategory.Private, query)
      .pipe(
        map((bookmarks) => { // restrict to bookmark's owned
          return bookmarks.filter((bookmark) => bookmark.is_owner);
        })
      );
  }

  /**
   * Gets a list of user favorited content, by favorite id.
   */
  getFavorites(): Observable<BookmarkModel[]> {
    return this.http.get(favoritesServiceURL)
      .pipe(
        map((results: any) => {
          return Object.values(results)
            .map((result) => plainToClass(BookmarkModel, result))
            .map((bookmark) => this.normalizeBookmarkModel(bookmark));
        })
      );
  }

  /**
   * Deletes the specified content in the user's favorites.
   */
  deleteFavorite(contentId: string, contentType: string): Observable<any> {
    return this.http.delete(`${favoritesServiceURL}?modelId=${contentId}&category=${contentType}`);
  }

  /* Gets a list of user custom conditions.
  */
  getPrivateConditions(): Observable<BookmarkModel[]> {
    return this.getUserConditions(BookmarkCategory.Private);
  }

  /**
   * Gets a list of bookmarks for the specified organziation
   * @param organizationId The organization id for the bookmarks
   * @param query: Optional search query
   */
  getOrganizationBookmarks(organizationId: number, query: string = ''): Observable<BookmarkModel[]> {
    return this.getUserBookmarks(BookmarkCategory.Private, query)
      .pipe(
        map((bookmarks) => {
          // filter enterprise bookmarks
          return bookmarks
            .filter((bookmark) => bookmark.tags.includes('enterprise'))
            // restrict to group accessibility
            .filter((bookmark) => bookmark.group_access.some((access) => access.group_id === organizationId));
        })
      );
  }

  /**
 * Gets a list of group custom conditions.
 */
  getOrganizationConditions(organizationId: number) {
    return this.getUserConditions(BookmarkCategory.Group, organizationId);
  }

  /**
   * Gets all content (bookmarks, modules) for the specified organization
   */
  getOrganizationContent(organizationId: number, query: string = '') {
    return forkJoin(
      this.getOrganizationBookmarks(organizationId, query),
      this.getOrganizationConditions(organizationId)
    )
      .pipe(
        map(([bookmarks, conditions]) => [...bookmarks, ...conditions])
      );
  }

  // create a new bookmark object
  createBookmarkStory(inplace: boolean = false): { story: StoryModel, chapterIdMap: any } {
    return this.serializeModuleService.serialize(
      inplace,
      {},
      new TrimConfiguration(),
      false,
      this.store.selectSnapshot(StoryState.getStory),
      this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
      );
  }

  /**
   * Gets a list of public bookmarks
   * @param query: Optional search query
   */
  getPublicBookmarks(query: string = ''): Observable<BookmarkModel[]> {
    return this.getUserBookmarks(BookmarkCategory.Public, query);
  }

  saveBookmark(entry: BookmarkEntryModel): Observable<BookmarkModel> {
    // normalize to required service params
    const normalizedEntry = this.getNormalizedBookmarkEntry(entry);

    let response$: Observable<any>;
    if (entry.id) {
      // update bookmark
      response$ = this.http.post(`${bookmarkServiceURL}/update`, normalizedEntry, formHeaders);
    } else {
      // save new bookmark
      response$ = this.http.post(`${bookmarkServiceURL}/save`, normalizedEntry, formHeaders);
    }

    return response$.pipe(
      // normalize to bookmark model
      flatMap((response: any) => this.getBookmarkModel(response.id))
    );
  }

  deleteBookmark(id: string): Observable<any> {
    return this.http.delete(`${bookmarkServiceURL}/delete?eid=${id}`)
      .pipe(
        catchError((response) => {
          const { error } = response.error;
          const message = error ? error.message : '';
          return throwError(message);
        })
      );
  }

  copyBookmark(id: string, type: StoryType) {
    // Fix module id truncation
    if (type === StoryType.Module && !endsWith(id, '.json')) {
      id += '.json';
    }

    return this.http.post(`${bookmarkServiceURL}/copy`, { source_content_type: type, source_content_id: id })
      .pipe(
        map((response: any) => response.id)
      );
  }

  atMaxFreeBookmarkCount(): Observable<boolean> {
    return this.http.get(`/ws/user/content/limit/check`)
      .pipe(
        map(() => false),
        catchError((response) => of(response.status === 403))
      );
  }

  // save thumbnail data for each chapter in a bookmark
  saveBookmarkThumbnails(
    id: string,
    bookmarkEntry: BookmarkEntryModel,
    thumbnails: Map<string, StoryChapterThumbnail>,
    chapterIdMap: { [id: string]: string }
  ): Observable<any>[] {

    // chapter thumbnails
    const thumbnailRequests$ = bookmarkEntry.index.chapters.reduce<Observable<any>[]>((requests, chapter) => {
      const newChapterId = chapter.id;
      const oldChapterId = chapterIdMap[newChapterId];
      const currentThumbnail = thumbnails.get(oldChapterId);

      if (currentThumbnail && currentThumbnail.snapshot) {
        requests.push(this.postThumbnail(id, chapter.id, currentThumbnail.snapshot));
      } else if (chapter.id !== chapterIdMap[chapter.id] || currentThumbnail.chapterId !== chapter.id) {
        const splitIdx = currentThumbnail.urls.large.match(/(bookmarks\/)|(modules\/)/).index;
        const srcURL = currentThumbnail.urls.large.slice(splitIdx);
        requests.push(this.copyThumbnail(id, chapter.id, srcURL));
      }
      return requests;
    }, []);

    // bookmark gallery thumbnail
    const firstChapterThumbnail = thumbnails.get(chapterIdMap[bookmarkEntry.index.chapters[0].id]);
    if (firstChapterThumbnail.snapshot) {
      // create new
      thumbnailRequests$.push(this.postThumbnail(id, null, firstChapterThumbnail.snapshot));
    } else {
      // copy existing
      thumbnailRequests$.push(this.copyThumbnail(id, null, firstChapterThumbnail.urls.large));
    }

    return thumbnailRequests$;
  }

  // delete thumbnails for deleted chapters
  deleteBookmarkThumbnails(id: string, bookmarkEntry: BookmarkEntryModel,
    thumbnails: Map<string, StoryChapterThumbnail>, chapterIdMap: { [id: string]: string }): Observable<any>[] {
    /*
    delete existing (server stored) thumbnail urls on deleted chapters
    */
    const chapterIds = bookmarkEntry.index.chapters.map((chapter) => chapterIdMap[chapter.id] || chapter.id);
    const deleteRequests$ = Array.from(thumbnails.values()).filter((thumbnail) => chapterIds.indexOf(thumbnail.chapterId) < 0)
      .map((thumbnail) => {
        return this.deleteThumbnail(id, thumbnail.chapterId);
      });

    return deleteRequests$;
  }


  /**
   * Filters the specified list by tags
   * @param bookmarks The list of bookmarks
   * @param tags The list of tags
   * @param inclusive True to match all tags, else single match
   */
  filterByTags(
    bookmarks: BookmarkModel[],
    tags: string[],
    inclusive: boolean = false
  ): BookmarkModel[] {
    return bookmarks.filter((bookmark) => {
      if (inclusive) {
        return tags.every((tag) => bookmark.tags.includes(tag));
      } else {
        return tags.some((tag) => bookmark.tags.includes(tag));
      }
    });
  }

  canEdit(bookmark: BookmarkModel) {
    if (bookmark.is_owner) {
      return true;
    }

    if (bookmark.group_access && bookmark.group_access.length) {
      return bookmark.group_access[0].member_edit;
    }

    return false;
  }

  normalizeBookmarkModel(bookmark: BookmarkModel): BookmarkModel {
    const { id, type } = bookmark;
    // normalize id
    if (type === 'module') {
      bookmark.id = this.exploreService.toAppContentId(id);
    }
    // normalize tag properties
    bookmark.tour = bookmark.tags.includes('tour');
    bookmark.animated = bookmark.tags.includes('animated');

    return bookmark;
  }

  private getNormalizedBookmarkEntry(entry: BookmarkEntryModel): any {
    const { id, title, description, index, public_access, add_group_access, remove_group_access, isOwner } = entry;

    // optional user (owner) fields
    let group_access: any;
    let publicAccess: string;
    let flags: any;

    if (isOwner) {
      // set additional group access    
      group_access = add_group_access ? this.getGroupAccess(add_group_access) : [];

      // revoke group access
      if (remove_group_access) {
        remove_group_access.forEach((access) => {
          const removedAccess = this.getNormalizeGroupAccess({
            group_id: access.group_id,
            remove: true
          });
          group_access.push(removedAccess);
        });
      }

      // set public access
      publicAccess = this.getNormalizedBool(public_access);

      // custom flags
      if (index) {
        // init flags
        flags = {};

        // flag: user generated tours
        if (index.chapters) {
          const isTour = index.chapters.length > 1;
          flags['user_generated_tour'] = this.getNormalizedBool(isTour);
        }
        // flag: animated content
        const isAnimated = index.tags.includes('animated');
        flags['animated'] = this.getNormalizedBool(isAnimated);
      }
    }

    // normalized service entry
    return {
      eid: id,
      name: title,
      description: description,
      // serialized story
      index: index,
      // permissions: set null for no updates
      group_access: group_access,
      public_access: publicAccess,
      // custom bookmark flags
      flags: flags
    };
  }

  private getGroupAccess(groupAccess: BookmarkGroupModel[]): any {
    return groupAccess.map((access) => this.getNormalizeGroupAccess(access));
  }

  private getNormalizeGroupAccess(access: BookmarkGroupModel): any {
    const { group_id, group_name, member_edit, visible, remove } = access;

    const normalized = {
      group_id: group_id,
      group_name: group_name,
      member_edit: this.getNormalizedBool(member_edit),
      visibility: this.getNormalizedBool(visible),
      remove: this.getNormalizedBool(remove)
    };
    return normalized;
  }

  private getNormalizedBool(value: boolean): string {
    return value === true ? '1' : (value === false ? '0' : '');
  }

  // request to server to save a thumbnail
  private postThumbnail(storyId: string, chapterId: string, thumbnailURL: string): Observable<any> {
    const image = new Image();
    image.src = thumbnailURL;

    return new Observable((observer) => {
      image.onload = () => {
        // transfer image to canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        context.drawImage(image, 0, 0);

        // convert to data url
        const dataURL = canvas.toDataURL('image/jpeg', 1.0);

        // post request for thumbnail
        let _thumbnailURL = '';
        if (chapterId) {
          // chapter thumbnail
          _thumbnailURL = `${thumbnailServiceURL}/${storyId}/large/chapters/${chapterId}.jpg`;
        } else {
          // story thumbnail
          _thumbnailURL = `${thumbnailServiceURL}/${storyId}/large/index.jpg`;
        }

        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        this.http.post(_thumbnailURL, { image: dataURL }, { headers: headers, responseType: 'text' }).subscribe((r) => {
          observer.next();
          observer.complete();

        });
      };
    });
  }

  // request to server to delete a thumbnail
  private deleteThumbnail(storyId: string, chapterId: string): Observable<any> {
    return new Observable((observer) => {
      const request = new Request(`${thumbnailServiceURL}/${storyId}/large/chapters/${chapterId}.jpg`, { method: 'DELETE' });
      fetch(request).then((response) => {
        observer.complete();
      });
    });
  }

  // request to server to copy a thumbnail from one location to another
  private copyThumbnail(storyId: string, chapterId: string, src: string): Observable<any> {
    let dst = '';
    const srcURL = src.replace('/thumbs/', ''); // 
    if (chapterId) {
      // copy to chapter location
      dst = `bookmarks/${storyId}/large/chapters/${chapterId}.jpg`;
    } else {
      // copy to index location
      dst = `bookmarks/${storyId}/large/index.jpg`;
    }

    const header = new HttpHeaders().set('Content-Type', 'application/json');

    return new Observable((observer) => {
      this.http.post('/ws/thumbs/copy', { src: srcURL, dst: dst }, { headers: header, responseType: 'text' }).subscribe((r) => {
        observer.next();
        observer.complete();
      });
    });
  }
}
